/**
 * 検索条件 <-> URL クエリの相互変換
 * 既定値・空の条件はクエリに載せない。
 */

/** relevance (関連度順) は検索語があるときだけ選べる。検索語があるときの既定値 */
export type SongSort = 'new' | 'popular' | 'relevance';

/** 検索語の有無に応じた既定の並び順 */
export function defaultSongSort(q: string): SongSort {
  return q.trim() ? 'relevance' : 'new';
}

export interface SongQuery {
  q: string;
  type: string[];
  duration: string[];
  loop: string;
  mood: string[];
  rhythm: string[];
  instrument: string[];
  exclude: string;
  composer: string[];
  excludeComposer: string[];
  page: number;
  sort: SongSort;
}

export const DEFAULT_SONG_QUERY: SongQuery = {
  q: '',
  type: [],
  duration: [],
  loop: '',
  mood: [],
  rhythm: [],
  instrument: [],
  exclude: '',
  composer: [],
  excludeComposer: [],
  page: 1,
  sort: 'new',
};

function parsePage(v: string | null): number {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

export function parseSongQuery(params: URLSearchParams): SongQuery {
  const sort = params.get('sort');
  const q = params.get('q') ?? '';
  return {
    q,
    type: params.getAll('type'),
    duration: params.getAll('duration'),
    loop: params.get('loop') ?? '',
    mood: params.getAll('mood'),
    rhythm: params.getAll('rhythm'),
    instrument: params.getAll('instrument'),
    exclude: params.get('exclude') ?? '',
    composer: params.getAll('composer'),
    excludeComposer: params.getAll('xcomposer'),
    page: parsePage(params.get('page')),
    sort:
      sort === 'popular' || sort === 'new' || (sort === 'relevance' && q.trim())
        ? sort
        : defaultSongSort(q),
  };
}

export function songQueryToParams(q: SongQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  q.type.forEach((v) => p.append('type', v));
  q.duration.forEach((v) => p.append('duration', v));
  if (q.loop) p.set('loop', q.loop);
  q.mood.forEach((v) => p.append('mood', v));
  q.rhythm.forEach((v) => p.append('rhythm', v));
  q.instrument.forEach((v) => p.append('instrument', v));
  if (q.exclude) p.set('exclude', q.exclude);
  q.composer.forEach((v) => p.append('composer', v));
  q.excludeComposer.forEach((v) => p.append('xcomposer', v));
  if (q.page > 1) p.set('page', String(q.page));
  if (q.sort !== defaultSongSort(q.q)) p.set('sort', q.sort);
  return p;
}

/** 詳細条件 (詳細条件ボタンの配下) が 1 つでも指定されているか */
export function hasSongDetailFilters(q: SongQuery): boolean {
  return (
    q.mood.length > 0 ||
    q.rhythm.length > 0 ||
    q.instrument.length > 0 ||
    q.exclude !== '' ||
    q.composer.length > 0 ||
    q.excludeComposer.length > 0
  );
}

// ---- 作曲者検索 ------------------------------------------------------------

export type ComposerSort = 'new' | 'songs';

export interface ComposerQuery {
  q: string;
  type: string;
  count: string;
  usage: string;
  keyword: string;
  page: number;
  sort: ComposerSort;
}

export const DEFAULT_COMPOSER_QUERY: ComposerQuery = {
  q: '',
  type: '',
  count: '',
  usage: '',
  keyword: '',
  page: 1,
  sort: 'new',
};

export function parseComposerQuery(params: URLSearchParams): ComposerQuery {
  const sort = params.get('sort');
  return {
    q: params.get('q') ?? '',
    type: params.get('type') ?? '',
    count: params.get('count') ?? '',
    usage: params.get('usage') ?? '',
    keyword: params.get('keyword') ?? '',
    page: parsePage(params.get('page')),
    sort: sort === 'songs' ? 'songs' : 'new',
  };
}

export function composerQueryToParams(q: ComposerQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.type) p.set('type', q.type);
  if (q.count) p.set('count', q.count);
  if (q.usage) p.set('usage', q.usage);
  if (q.keyword) p.set('keyword', q.keyword);
  if (q.page > 1) p.set('page', String(q.page));
  if (q.sort !== 'new') p.set('sort', q.sort);
  return p;
}
